/**
 * Centralized HTTP client for all API calls.
 *
 * Provides:
 * - `API_BASE_URL`   – resolved at startup from the current hostname.
 * - `getAuthToken()` – stub que devuelve null (round 2026-04-26 fase 6 P1-B);
 *                      el JWT vive en cookie HttpOnly + memoria del AuthContext.
 * - `apiFetch()`     – wraps fetch with auth (cookie), JSON parsing y Ant Design messages.
 */

import { getMsg, getModal } from "../utils/antdStatic";
import { broadcastLogout } from "../utils/authBroadcast";

// ─── Base URL ────────────────────────────────────────────────────────────────

function resolveApiBaseUrl(): string {
  const envUrl = process.env.REACT_APP_API_URL?.trim();
  if (envUrl) {
    return envUrl.replace(/\/+$/, "") + "/api";
  }

  const { hostname, protocol } = window.location;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//localhost:3001/api`;
  }

  return "/api";
}

export const API_BASE_URL = resolveApiBaseUrl();

// Capturamos `window.fetch` antes del monkey-patch de abajo, para que
// `forceLogout` y otros call sites internos puedan llamar al original sin
// reentrar al interceptor. Round 2026-04-26 fase 6.
const _originalFetch = window.fetch.bind(window);

// ─── Token helper ─────────────────────────────────────────────────────────────

/**
 * Stub que devuelve null.
 *
 * Round 2026-04-26 fase 6 (P1-B): la fuente de verdad de auth es la
 * cookie HttpOnly `authToken` (set por el backend en login). El JWT ya
 * NO se persiste en localStorage/sessionStorage — quedaba expuesto a XSS
 * sin necesidad. Mantenemos el export para no romper imports residuales
 * de paths como DocumentViewerPage que lo usaban como auth defensivo
 * adicional (los `/files/<JWT>` ya están firmados en el path).
 *
 * Si necesitas el token vivo (p.ej. para el WebSocket que lo pasa por
 * `Sec-WebSocket-Protocol`), usa `useAuth().token` — vive solo en memoria.
 */
export function getAuthToken(): string | null {
  return null;
}

/**
 * CSRF token en memoria del módulo. Lo seteamos al login y al rehidratar
 * (`/auth/me`) leyendo el body de la respuesta del backend, NO la cookie.
 *
 * Round 2026-04-27 — fix: cuando frontend y backend están en orígenes
 * distintos (dev: localhost:3000 vs localhost:3001 — prod: subdominios
 * Railway diferentes), `document.cookie` NO ve la cookie `csrfToken`
 * porque pertenece al origen del backend (cross-origin cookies son opacas
 * para JS aunque viajen en `credentials:"include"`). El backend ya
 * devolvía el csrf en el body de login/me precisamente para este caso —
 * sólo faltaba que el frontend lo guardara y lo usara.
 *
 * La cookie `csrfToken` sigue siendo necesaria: el backend la lee del
 * Cookie header y la compara con el `X-CSRF-Token` que mandamos
 * (double-submit pattern). Pero el frontend ya no intenta leerla.
 */
let csrfTokenInMemory: string | null = null;

/**
 * Guarda el csrfToken devuelto por `/auth/login` o `/auth/me` para que
 * `apiFetch` pueda mandarlo como header en mutaciones siguientes.
 */
export function setCsrfToken(token: string | null): void {
  csrfTokenInMemory = token;
}

/**
 * Devuelve el csrfToken vivo. Primero memoria (set por login/me), luego
 * fallback a `document.cookie` por si el deploy llegara a quedar en
 * mismo-origen (en cuyo caso JS sí podría leer la cookie).
 */
export function getCsrfToken(): string | null {
  if (csrfTokenInMemory) return csrfTokenInMemory;
  if (typeof document === "undefined") return null;
  const m = /(?:^|;\s*)csrfToken=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Re-sincroniza el `csrfToken` en memoria con la cookie vigente pidiendo
 * `/auth/me`. El backend devuelve en el body el csrf de la cookie ACTUAL (lo
 * reusa, no lo rota — ver auth.controller.js), así memoria := cookie y la
 * próxima mutación matchea. Devuelve el csrf fresco, o null si la sesión ya no
 * es válida (401 en /auth/me → el interceptor global NO fuerza logout ahí).
 *
 * Se usa como auto-recuperación cuando una mutación recibe 403 "CSRF token
 * inválido": el header y la cookie quedaron desincronizados (típico tras un
 * re-login en otra pestaña, que ROTA la cookie csrf pero deja la memoria de
 * esta tab con el valor viejo). Round 2026-07-24.
 */
async function refreshCsrfToken(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/me`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    const fresh = typeof data?.csrfToken === "string" ? data.csrfToken : null;
    if (fresh) setCsrfToken(fresh);
    return fresh;
  } catch {
    return null;
  }
}

/**
 * Clears all auth data from storage and redirects to login.
 * Used when API returns 401 (token expired or invalid).
 *
 * Round 2026-04-26 fase 6: además del clear local, llamamos a
 * `/api/auth/logout` para que el backend limpie las cookies HttpOnly
 * (no podemos borrarlas desde JS). El POST es fire-and-forget — no
 * bloqueamos el redirect si falla.
 */
function forceLogout(): void {
  // Round 2026-04-26 fase 6 (P1-B): el JWT ya no vive en storage; solo
  // limpiamos `user` (cacheado por AuthContext para hidratación rápida).
  // Round 2026-10-28: cada acceso a storage va en su propio try/catch. `localStorage` LANZA con los
  // datos del sitio bloqueados, en ventanas privadas de algunos navegadores o con la cuota llena;
  // si lanzaba aquí, la función moría antes de la redirección y la sesión caída dejaba la pantalla
  // congelada en vez de mandar a /login.
  try {
    localStorage.removeItem("user");
  } catch {
    /* sin storage: no hay nada que limpiar */
  }
  try {
    sessionStorage.removeItem("user");
  } catch {
    /* idem */
  }
  // Limpiar el csrfToken en memoria — round 2026-04-27. Si la sesión
  // cae, el siguiente login traerá uno fresco; dejarlo viejo no
  // serviría (la cookie ya quedó invalidada por el backend al limpiar).
  csrfTokenInMemory = null;
  // Avisar a las demás tabs que la sesión cayó — round 2026-04-27. La
  // cookie HttpOnly que está por borrarse es cross-tab, pero el `token`
  // en memoria de cada tab es independiente; sin broadcast quedan
  // mostrando UI logueada hasta que hagan una request que reciba 401.
  broadcastLogout();
  // Best-effort: pegar al backend para clear cookies. Si falla, redirigimos igual.
  try {
    _originalFetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
  } catch {}
  window.location.href = "/login";
}

// ─── Global fetch interceptor ────────────────────────────────────────────────
// Many pages call fetch() directly instead of apiFetch(). El interceptor:
//   1. Inyecta `credentials: "include"` por default para que las cookies
//      HttpOnly viajen también en `fetch()` directos a `/api/*` y a `/files/*` (round
//      2026-04-26 fase 6 — P1-B). Sin esto, los call sites legacy
//      perderían la sesión.
//   2. Captura 401 hacia `/api/*` y dispara forceLogout — evita que el
//      usuario se quede con UI parcial luego de que el token expire.
//      Un 401 de `/files/*` NO desloguea (ver más abajo).

/** Resuelve una URL (absoluta o relativa a la página); `null` si no es una URL válida. */
function resolverUrl(url: string): URL | null {
  try {
    return new URL(url, window.location.href);
  } catch {
    return null;
  }
}

/**
 * ¿La petición va a NUESTRO backend, y a cuál de sus dos familias de rutas?
 *
 *   - `"api"`:   `<origen>/api` o `<origen>/api/…`
 *   - `"files"`: `<origen>/files/…` (archivos con URL firmada)
 *   - `null`:    cualquier otra cosa (Cloudflare, Stays, Turnstile, fuentes…)
 *
 * «Nuestro origen» es el de la API (`API_BASE_URL`: en producción `api.mialoja.cl`, que no es el de la
 * página) o el de la propia página. Se compara el ORIGEN y el PATH ya parseados, nunca un `includes`
 * sobre el texto: `url.includes("/api")` también calzaba con `https://api.mialoja.cl/files/abc`
 * (por el `//api.` del host) y con `https://api.mialoja.cl/health`, así que un 401 de un archivo de
 * `/files` —un token firmado vencido, con la sesión perfectamente viva— cerraba la sesión de la
 * persona (round 2026-10-28). Exportada para probarla.
 */
export function clasificarUrlPropia(url: string): "api" | "files" | null {
  const u = resolverUrl(url);
  if (!u) return null;
  const propios = new Set<string>();
  const api = resolverUrl(API_BASE_URL);
  const pagina = resolverUrl(window.location.href);
  if (api) propios.add(api.origin);
  if (pagina) propios.add(pagina.origin);
  if (!propios.has(u.origin)) return null;
  const ruta = u.pathname;
  const prefijoApi = (api?.pathname ?? "/api").replace(/\/+$/, "") || "/api";
  const esApi = (p: string) => ruta === p || ruta.startsWith(`${p}/`);
  if (esApi("/api") || esApi(prefijoApi)) return "api";
  if (ruta.startsWith("/files/")) return "files";
  return null;
}

window.fetch = async (...args: Parameters<typeof fetch>) => {
  const url =
    typeof args[0] === "string"
      ? args[0]
      : args[0] instanceof URL
        ? args[0].href
        : typeof Request !== "undefined" && args[0] instanceof Request
          ? args[0].url
          : "";
  // Solo enriquecemos llamadas a nuestra API o a /files; requests externos
  // (Cloudflare, Stays, Turnstile) se respetan tal cual.
  const tipo = clasificarUrlPropia(url);
  const isLocal = tipo !== null;
  if (isLocal && args[1] && typeof args[1] === "object") {
    const init = args[1] as RequestInit;
    if (init.credentials === undefined) {
      args[1] = { ...init, credentials: "include" };
    }
  } else if (isLocal && !args[1]) {
    args[1] = { credentials: "include" };
  }
  const res = await _originalFetch(...args);
  // Excepción: `/api/auth/me` es justo para "verificar si tengo sesión".
  // Un 401 ahí significa "no estás logueado" — el caller (AuthContext)
  // decide qué hacer (limpiar cache, no redirigir si ya estás en /login).
  // Disparar forceLogout aquí causaría un redirect en /login → loop.
  const ruta = resolverUrl(url)?.pathname ?? "";
  const isAuthMe = ruta.includes("/api/auth/me");
  // Excepción 2 (round 2026-10-28): `/api/auth/login` responde 401 cuando la CLAVE ES
  // INCORRECTA — no es una sesión caída. Antes caía aquí, disparaba forceLogout y hacía
  // `window.location.href = "/login"`: una recarga completa de la pantalla en la que el
  // usuario estaba, que borraba el mensaje de error y lo que había tipeado. Desde afuera: «le
  // doy a Iniciar sesión y no pasa nada». En la app instalada del celular (sin barra de
  // navegación ni indicador de carga) es indistinguible de un cuelgue. El login maneja su 401
  // por su cuenta (ver authApi.login + utils/loginErrors.ts).
  const isAuthLogin = ruta.includes("/api/auth/login");
  // Excepción 3 (round 2026-10-28): un 401 de `/files/*` NO es una sesión caída. La URL de un
  // archivo va firmada y esa firma vence (ventana de 6 h) o el archivo cambia, mientras la sesión
  // sigue viva; si de verdad cayó la sesión, la próxima llamada a `/api` recibe su propio 401 y
  // cierra la sesión ahí. Quien pidió el archivo (descarga, visor) muestra su mensaje.
  if (res.status === 401 && tipo === "api" && !isAuthMe && !isAuthLogin) {
    forceLogout();
  }
  return res;
};

// ─── apiFetch ─────────────────────────────────────────────────────────────────

/**
 * Lo que el llamador puede saber de una llamada que falló (round 2026-10-30): `apiFetch` nunca lanza y devuelve `null`, así que
 * sin esto un formulario no sabe POR QUÉ falló (p. ej. un 409 `TELEFONO_DUPLICADO` que debe marcar el campo del teléfono).
 */
export interface ErrorApi {
  /** Estado HTTP, o `null` si no llegó respuesta (red caída, JSON ilegible). */
  status: number | null;
  /** Código de máquina del servidor (`code`, suelto o dentro de `error`), o `null`. */
  code: string | null;
  /** El mismo texto que se muestra al usuario. */
  message: string;
}

/** Lo único de `ApiFetchOptions` que las funciones de `*.api.ts` dejan pasar al llamador (p. ej. un formulario). */
export interface OpcionesLlamada {
  alFallar?: (error: ErrorApi) => void;
}

/** Error HTTP con el estado y el código que dio el servidor (solo viaja del `throw` al `catch` de `apiFetch`). */
class ErrorHttp extends Error {
  status: number;
  code: string | null;
  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = "ErrorHttp";
    this.status = status;
    this.code = code;
  }
}

interface ApiFetchOptions extends RequestInit {
  /** If provided, `message.success` is shown on a successful 2xx response. */
  successMsg?: string;
  /**
   * Shown via `message.error` when the request fails.
   * Defaults to the error message returned by the server.
   */
  errorMsg?: string;
  /**
   * Si `true`, muestra el error como Modal.error persistente en vez de un
   * toast (`message.error`) que dura ~3s y es fácil de no ver. Útil cuando
   * el mensaje trae info accionable (p.ej. "ya existe la propiedad X (ID Y)").
   */
  errorAsModal?: boolean;
  /**
   * Si `true`, NO muestra ningún toast/modal de error (sigue devolviendo
   * `null` y logueando en consola). Útil para lecturas de fondo opcionales
   * (ej. un ajuste de config que puede no existir todavía en el backend) en
   * las que un toast de error sería ruido para el usuario.
   */
  silent?: boolean;
  /**
   * Se llama UNA vez cuando la petición falla (después del aviso al usuario, que no se quita): le da al formulario el estado y
   * el `code` del servidor para marcar el campo que falló. Si lanza, se ignora (`apiFetch` nunca lanza).
   */
  alFallar?: (error: ErrorApi) => void;
}

/**
 * Thin wrapper around `fetch` that:
 * - Prepends `API_BASE_URL` to the path.
 * - Attaches the `Authorization: Bearer <token>` header automatically.
 * - Adds `Content-Type: application/json` for non-FormData bodies.
 * - Parses the JSON response and returns typed data on success.
 * - Shows Ant Design `message.success` / `message.error` toasts.
 * - Returns `null` on any error (never throws).
 *
 * @example
 * const data = await apiFetch<CleanerResponse>("/users/cleaners");
 * const result = await apiFetch<ApiResponse<any>>("/users/1", {
 *   method: "PUT",
 *   body: JSON.stringify(payload),
 *   successMsg: "Usuario actualizado correctamente",
 * });
 */
export async function apiFetch<T = any>(
  path: string,
  { successMsg, errorMsg, errorAsModal, silent, alFallar, ...init }: ApiFetchOptions = {},
): Promise<T | null> {
  const token = getAuthToken();
  const isFormData = init.body instanceof FormData;
  const method = (init.method ?? "GET").toUpperCase();
  const isMutation = method !== "GET" && method !== "HEAD" && method !== "OPTIONS";

  // Headers frescos en cada intento (así un reintento toma el csrf ya
  // re-sincronizado). CSRF: en mutaciones mandamos `X-CSRF-Token` (double-submit).
  // El backend exige que matchee la cookie `csrfToken` cuando la auth es por
  // cookie HttpOnly; con Bearer legacy lo ignora. `forceCsrf` sobreescribe el
  // header con el token fresco en el reintento.
  const buildHeaders = (forceCsrf: boolean): Headers => {
    const headers = new Headers(init.headers as HeadersInit);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (!isFormData && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (isMutation) {
      const csrf = getCsrfToken();
      if (csrf && (forceCsrf || !headers.has("X-CSRF-Token"))) {
        headers.set("X-CSRF-Token", csrf);
      }
    }
    return headers;
  };

  // `credentials: "include"` envía las cookies HttpOnly `authToken` + `csrfToken`.
  const doFetch = (forceCsrf: boolean): Promise<Response> =>
    fetch(`${API_BASE_URL}${path}`, {
      credentials: "include",
      ...init,
      headers: buildHeaders(forceCsrf),
    });

  try {
    let res = await doFetch(false);

    // Auto-recuperación CSRF (round 2026-07-24): un 403 con mensaje de CSRF en
    // una mutación = el header `X-CSRF-Token` quedó DESINCRONIZADO de la cookie
    // `csrfToken` (típico tras un re-login en otra pestaña que rota la cookie,
    // o una sesión renovada). El chequeo CSRF corre en `verifyToken` ANTES del
    // controller, así que la request rechazada NO tuvo efecto → es seguro
    // re-sincronizar el token vía /auth/me y reintentar UNA vez. Sin esto el
    // usuario perdía la acción (ej. crear una reserva) con "CSRF token inválido".
    if (res.status === 403 && isMutation) {
      const peek = await res.clone().json().catch(() => null);
      const peekMsg =
        peek?.message ||
        (typeof peek?.error === "string" ? peek.error : peek?.error?.message) ||
        "";
      if (/csrf/i.test(peekMsg)) {
        const fresh = await refreshCsrfToken();
        if (fresh) res = await doFetch(true);
      }
    }

    if (res.status === 401) {
      forceLogout();
      return null as T;
    }

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      // Tres formatos de body pueden venir en error:
      //   - controller con { message }: { success:false, message:"..." }
      //   - controller con { error:string }: { success:false, error:"..." }
      //     (usado por stays.recalc.controller, varios endpoints viejos)
      //   - global handler (app.js): { success:false, error:{code,message} }
      // Round 2026-05-10: agregamos el caso de `err.error` como string —
      // antes caía al fallback "Error 404/409/500" y el admin no veía
      // por qué falló (ej. "No hay payload en stays_events para esta
      // reserva" se perdía en el botón Recalcular).
      const serverMsg =
        err?.message ||
        (typeof err?.error === "string" ? err.error : err?.error?.message);
      const codigo =
        typeof err?.code === "string"
          ? err.code
          : typeof err?.error?.code === "string"
            ? err.error.code
            : null;
      throw new ErrorHttp(serverMsg || `Error ${res.status}`, res.status, codigo);
    }

    // 204 No Content y 205 Reset Content nunca traen body — algunos
    // controllers (ej. `deleteTask`) responden 204. Antes hacíamos
    // `res.json()` ciegamente y rompía con "Unexpected end of JSON input".
    // También chequeamos `Content-Length: 0` por si llega 200 sin body.
    const contentLength = res.headers.get("Content-Length");
    if (res.status === 204 || res.status === 205 || contentLength === "0") {
      if (successMsg) getMsg()?.success(successMsg);
      return null as T;
    }

    const data: T = await res.json();
    if (successMsg) getMsg()?.success(successMsg);
    return data;
  } catch (e) {
    console.error(`[apiFetch] ${path}:`, e);
    const text =
      e instanceof Error ? e.message : (errorMsg ?? "Error desconocido");
    // El formulario se entera aunque el aviso sea silencioso: el aviso y el campo marcado son cosas distintas.
    if (alFallar) {
      try {
        alFallar({
          status: e instanceof ErrorHttp ? e.status : null,
          code: e instanceof ErrorHttp ? e.code : null,
          message: text,
        });
      } catch (errCallback) {
        console.error(`[apiFetch] ${path}: alFallar lanzó`, errCallback);
      }
    }
    // `silent`: lecturas de fondo opcionales que no deben molestar con toasts.
    if (silent) return null;
    if (errorAsModal) {
      const modal = getModal();
      if (modal) {
        modal.error({ title: "No se pudo completar la operación", content: text });
      } else {
        getMsg()?.error(text);
      }
    } else {
      getMsg()?.error(text);
    }
    return null;
  }
}
